<template>
  <div class="screen" :class="{ 'screen--table': isTable }">
    <div v-if="roomError" class="notice notice--bad">{{ roomError }}</div>

    <template v-if="!roomId">
      <p class="counter-band">Opening the room…</p>
      <button class="secondary" @click="$emit('back')">Back</button>
    </template>

    <template v-else-if="!roomMeta">
      <p class="counter-band">Joining…</p>
    </template>

    <!--
      The big screen's lobby: point a camera at it and you are in. The code
      stays, big, for anyone whose camera will not play along.
    -->
    <template v-else-if="isTable">
      <p class="table-lobby__label">Scan to join</p>
      <div class="table-lobby__scan">
        <QrCode class="table-lobby__qr" :value="joinLink" />
        <div class="table-lobby__aside">
          <span class="table-lobby__or">or type the code</span>
          <div class="table-lobby__code">{{ roomMeta.roomCode }}</div>
          <span class="table-lobby__hint">{{ joinUrl }}</span>
        </div>
      </div>
      <!-- Someone far away opens ONE link on both their phone and their TV;
           it asks which screen it is on (Matt, 2026-10-20). -->
      <div class="table-lobby__share">
        <span class="table-lobby__or">Friends far away: one link for their phone and their TV</span>
        <div class="table-lobby__link">
          <strong>{{ roomUrl }}</strong>
          <button class="secondary" type="button" @click="copyLink">{{ copyLabel }}</button>
        </div>
      </div>
      <ul class="table-lobby__who">
        <li v-for="slot in seats" :key="slot.uid">{{ slot.displayName }}</li>
        <li v-if="!seats.length" class="table-lobby__waiting">Nobody yet.</li>
      </ul>
      <ul class="role-tiles role-tiles--table">
        <li v-for="role in ROLES" :key="role" class="role-tile" :style="{ '--role': ROLE_INFO[role].colour }">
          <span class="role-tile__name">{{ ROLE_INFO[role].label }}</span>
          <span class="role-tile__holder">{{ holderName(role) || (seats.length ? 'whoever is first in' : 'unclaimed') }}</span>
        </li>
      </ul>
      <label v-if="isHost" class="bots-toggle">
        <input id="bots-toggle-table" type="checkbox" :checked="Boolean(roomMeta.bots)" @change="$store.dispatch('setRoomBots', $event.target.checked)">
        <span>Bots play the roles nobody claims <small>for playtesting one role on your own</small></span>
      </label>
      <button v-if="isHost" class="primary" :disabled="!seats.length" @click="deal">
        Start the game
        <small class="action__sub">{{ !seats.length ? 'Waiting for a phone to scan in.' : (roomMeta.bots ? 'Bots play the roles nobody picked.' : 'Roles nobody picked go to the first phone in.') }}</small>
      </button>
      <p v-else class="counter-band">Waiting for the host to start…</p>
    </template>

    <template v-else>
      <div class="room-code">
        <span class="room-code__label">Room code</span>
        <strong class="room-code__value">{{ roomMeta.roomCode }}</strong>
        <button class="secondary share-button" @click="share">{{ shareLabel }}</button>
        <span class="room-code__sub">One link: open it on your phone and your TV.</span>
      </div>

      <ul class="roster">
        <li v-for="slot in seats" :key="slot.uid" class="roster__row">
          <span class="roster__name">{{ slot.displayName }}</span>
          <span v-if="slot.uid === roomMeta.hostUid" class="roster__tag">host</span>
          <span v-if="slot.uid === myUid" class="roster__tag">you</span>
          <span class="roster__dot" :class="{ 'roster__dot--on': presence[slot.uid] }" />
        </li>
      </ul>

      <div class="field">
        <label>Pick your role - tap to claim, tap again to let go. One person can hold several.</label>
        <ul class="role-tiles">
          <li v-for="role in ROLES" :key="role">
            <button
              class="role-tile"
              :class="{ 'role-tile--mine': holder(role) === myUid, 'role-tile--taken': holder(role) && holder(role) !== myUid }"
              :style="{ '--role': ROLE_INFO[role].colour }"
              :disabled="holder(role) && holder(role) !== myUid"
              @click="toggle(role)"
            >
              <span class="role-tile__name">{{ ROLE_INFO[role].label }}</span>
              <span class="role-tile__tag">{{ ROLE_INFO[role].tagline }}</span>
              <span class="role-tile__holder">{{ holderName(role) || 'unclaimed' }}</span>
            </button>
          </li>
        </ul>
      </div>

      <p class="counter-band">
        <template v-if="unclaimed.length && roomMeta.bots">
          Bots play the roles nobody claims.
        </template>
        <template v-else-if="unclaimed.length">
          Unclaimed roles go to {{ hostAtTable ? 'the first phone in' : 'the host' }} when the game starts.
        </template>
        <template v-else>Every role is taken.</template>
        To put the world on a TV, open
        <strong>{{ roomUrl }}</strong> there and pick "The shared table".
      </p>

      <!-- A solo playtest: claim your one role, let bots play the rest
           (Matt, 2026-10-12). Host only; the host's screen drives them. -->
      <label v-if="isHost" class="bots-toggle">
        <input id="bots-toggle" type="checkbox" :checked="Boolean(roomMeta.bots)" @change="$store.dispatch('setRoomBots', $event.target.checked)">
        <span>Bots play the roles nobody claims <small>for playtesting one role on your own</small></span>
      </label>
      <button v-if="isHost" class="primary" @click="deal">Start</button>
      <!-- Say WHO starts it (the learner: "waiting for the host" - who is
           that? When a TV opened the room, it is whoever is at the TV). -->
      <p v-else class="counter-band">{{ hostAtTable ? 'When everyone is in, someone at the TV presses Start the game.' : `Waiting for ${hostName} to start the game…` }}</p>
      <button class="secondary" @click="leave">Leave room</button>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { ROLES, ROLE_INFO } from '@/engine/constants';
import { playersIn } from '@/store/multiplayer';
import QrCode from '@/components/shared/QrCode.vue';
import { shareRoomLinks, copyRoomLink } from '@/utils/roomLinks.js';

export default {
  name: 'Lobby',
  components: { QrCode },
  emits: ['back'],
  data() {
    return { shareLabel: 'Share the link', copyLabel: 'Copy', ROLES, ROLE_INFO };
  },
  computed: {
    ...mapState(['roomId', 'roomMeta', 'presence', 'roomError']),
    ...mapGetters(['isHost', 'myUid', 'isTable']),
    seats() {
      // The table may hold a slot of its own; it is not a player.
      return playersIn(this.roomMeta);
    },
    // A room the TV opened has no host in a seat.
    hostAtTable() { return Boolean(this.roomMeta) && !this.seats.some((s) => s.uid === this.roomMeta.hostUid); },
    hostName() { return this.seats.find((s) => s.uid === this.roomMeta?.hostUid)?.displayName || 'the host'; },
    unclaimed() {
      return ROLES.filter((r) => !this.holder(r));
    },
    joinUrl() {
      return `${window.location.host}/?join=${this.roomMeta?.roomCode}`;
    },
    joinLink() {
      return `${window.location.origin}/?join=${this.roomMeta?.roomCode}`;
    },
    roomUrl() {
      return `${window.location.host}/?room=${this.roomMeta?.roomCode}`;
    },
  },
  mounted() {
    this.$store.dispatch('sweepRooms');
  },
  methods: {
    holder(role) { return this.roomMeta?.roles?.[role] || null; },
    holderName(role) {
      const uid = this.holder(role);
      if (!uid) return '';
      return this.seats.find((s) => s.uid === uid)?.displayName || 'someone';
    },
    toggle(role) { this.$store.dispatch('toggleRole', role); },
    deal() { this.$store.dispatch('dealRoom'); },
    leave() { this.$store.dispatch('leaveRoom'); this.$emit('back'); },
    async share() {
      try {
        if ((await shareRoomLinks(this.roomMeta?.roomCode)) === 'copied') {
          this.shareLabel = 'Copied';
          window.setTimeout(() => { this.shareLabel = 'Share the link'; }, 1800);
        }
      } catch {
        this.shareLabel = 'Share the link';
      }
    },
    async copyLink() {
      try {
        await copyRoomLink(this.roomMeta?.roomCode);
        this.copyLabel = 'Copied';
      } catch {
        this.copyLabel = 'Could not copy';
      }
      window.setTimeout(() => { this.copyLabel = 'Copy'; }, 1800);
    },
  },
};
</script>
